// apps/admin/src/pages/BulkUpload/utils/markdownToRichText.ts
import type ExcelJS from 'exceljs';

const LINK_FONT: Partial<ExcelJS.Font> = {
  color: { argb: 'FF0563C1' },
  underline: true,
};

const URL_FONT: Partial<ExcelJS.Font> = {
  size: 9,
  color: { argb: 'FF808080' },
};

const BOLD_FONT: Partial<ExcelJS.Font> = { bold: true };

const FULL_LINE_LINK_RE = /^\[([^\]]+)\]\(([^)]+)\)$/;
const INLINE_RE = /(\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\))/g;
const BOLD_ONLY_RE = /(\*\*(.+?)\*\*)/g;

export function markdownToRichText(
  markdown: string,
  options?: { keepLinkSyntax?: boolean },
): ExcelJS.CellRichTextValue {
  if (markdown === '') {
    return { richText: [{ text: '' }] };
  }

  const keepLinks = options?.keepLinkSyntax ?? false;
  const lines = markdown.split('\n');
  const runs: ExcelJS.RichText[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isLastLine = i === lines.length - 1;
    const suffix = isLastLine ? '' : '\n';

    if (!keepLinks) {
      const fullLinkMatch = line.match(FULL_LINE_LINK_RE);
      if (fullLinkMatch) {
        const [, text, url] = fullLinkMatch;
        runs.push({ text, font: { ...LINK_FONT } });
        runs.push({ text: `\n${url}${suffix}`, font: { ...URL_FONT } });
        continue;
      }
    }

    let processedLine = line;
    if (/^- /.test(line)) {
      processedLine = '• ' + line.slice(2);
    } else if (/^\d+\. /.test(line)) {
      processedLine = '  ' + line;
    }

    const lineRuns = parseInlineElements(processedLine, keepLinks);

    if (lineRuns.length > 0 && suffix) {
      const lastRun = lineRuns[lineRuns.length - 1];
      if (lastRun.font) {
        lineRuns.push({ text: suffix });
      } else {
        lastRun.text += suffix;
      }
    }

    runs.push(...lineRuns);
  }

  return { richText: runs };
}

function parseInlineElements(line: string, keepLinks: boolean): ExcelJS.RichText[] {
  const runs: ExcelJS.RichText[] = [];
  let lastIndex = 0;

  const re = keepLinks ? new RegExp(BOLD_ONLY_RE.source, 'g') : new RegExp(INLINE_RE.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = re.exec(line)) !== null) {
    if (match.index > lastIndex) {
      runs.push({ text: line.slice(lastIndex, match.index) });
    }

    if (match[2] !== undefined) {
      runs.push({ text: match[2], font: { ...BOLD_FONT } });
    } else if (!keepLinks && match[3] !== undefined && match[4] !== undefined) {
      runs.push({ text: match[3], font: { ...LINK_FONT } });
      runs.push({ text: `\n${match[4]}`, font: { ...URL_FONT } });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < line.length) {
    runs.push({ text: line.slice(lastIndex) });
  }

  if (runs.length === 0) {
    runs.push({ text: line });
  }

  return runs;
}

const LINK_RE_GLOBAL = /\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * リンク記法 [text](url) はそのまま保持しつつ、
 * 太字・箇条書き等の書式は Excel の RichText に変換して返す。
 */
export function markdownToCellValue(markdown: string): ExcelJS.CellRichTextValue {
  const hasLinks = LINK_RE_GLOBAL.test(markdown);
  LINK_RE_GLOBAL.lastIndex = 0;

  return markdownToRichText(markdown, { keepLinkSyntax: hasLinks });
}
